import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription, of } from 'rxjs';
import { OnDestroy, Input, HostListener, Directive } from '@angular/core';
import { Field, SquidexDate, Content, BaseFacade, DataParser, FormValidationsService, LanguageModel } from "../services/";
import { FormControl, ValidatorFn, Validators, FormGroup } from "@angular/forms";
import { SiteSettingModel } from '../services/akita/index';
import { environment } from '../../environments/environment.prod';
import { map } from 'rxjs/operators';
import { AppToken } from '../models/app-token';
import cloneDeep from 'lodash.clonedeep';

const EMPTY = '';

declare var CKEDITOR: any;

export abstract class BaseComponent implements OnDestroy {
  [x: string]: any;

  @Input()
  siteSetting: Observable<SiteSettingModel>;

  pageTitle: string;
  scrHeight: any;
  scrWidth: any;

  protected subscriptions: Subscription[];
  protected readonly statusDraft = 'Draft';
  protected readonly statusPublished = 'Published';
  protected language: string;
  protected readonly dParser: DataParser;
  private translateContext: string;

  currentPageNumber: number = 0;
  paginateSize: number = AppToken.CONTENTS_ITEMS_PER_PAGE;
  contentCount: number;
  filterValue: string = '';

  siteLabelElem: any = document.getElementById('previewSiteLabel');
  siteUrlElem: any = document.getElementById('previewSiteUrl');
  pageUrlElement: any = document.getElementById('currentPreviewUrl');


  protected constructor(
    protected readonly translateService: TranslateService) {
    this.getScreenSize();
    this.dParser = new DataParser(null);
    this.subscriptions = [];

    this.pageUrlElement = document.getElementById('currentPreviewUrl');
    this.siteUrlElem = document.getElementById('previewSiteUrl');
    this.siteLabelElem = document.getElementById('previewSiteLabel');
  }

  protected setTranslateContext(context: string): void {
    if (context) {
      context = context.toLowerCase().replace('component', EMPTY);
      this.translateContext = `COMPONENTS.${context}`.toUpperCase();
    }
  }

  protected clone(value: object): any {
    return cloneDeep(value);
  }

  @HostListener('window:resize', ['$event'])
  getScreenSize(event?) {
    this.scrHeight = window.innerHeight;
    this.scrWidth = window.innerWidth;
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }
  //translate global/general
  t(key?: string, interpolateParams?: Object): Observable<string | object> {
    if (!key) {
      return this.translateService.get('T', interpolateParams);
    }
    return this.translateService.get(`T.${key}`, interpolateParams);
  }

  //translate with context
  tc(key?: string, interpolateParams?: Object): Observable<string | object> {
    if (!key) {
      return this.translateService.get(`T.${this.translateContext}`, interpolateParams);
    }
    return this.translateService.get(`T.${this.translateContext}.${key}`, interpolateParams);
  }

  //translate with schema name and return key if not found
  ts(key?: string, schemaName?: string, interpolateParams?: Object): Observable<string | object> {
    let context = '';
    if (!key) {
      return of('');
    }

    let getKey: string;

    if (key.includes('.')) {
      getKey = `T.COMPONENTS.${key}`;
    } else {
      if (schemaName) {
        context = schemaName;
      } else if (this.selectedContent) {
        context = this.selectedContent.schemaName;
      } else if (this.translateContext) {
        context = this.translateContext;
      } else {
        return of(key);
      }

      getKey = `T.COMPONENTS.${context.replace('COMPONENTS.', '').toUpperCase()}.${key.replace(' ', '_').toUpperCase()}`;
    }

    return this.translateService.get(getKey, interpolateParams).pipe(map((lang) => {
      if (lang === getKey) {
        lang = key;
      }
      return lang;
    })) as Observable<string | object>;
  }

  //translate with schema name field name if not found
  tsl(key?: string, schemaName?: string, interpolateParams?: Object, fieldName?: string): Observable<string | object> {
    let context = '';
    if (!key) {
      if (fieldName) {
        return of(fieldName);
      } else {
        return of('');
      }
    }

    let getKey: string;

    if (key.includes('.')) {
      getKey = `T.COMPONENTS.${key}`;
    } else {
      if (schemaName) {
        context = schemaName;
      } else if (this.selectedContent) {
        context = this.selectedContent.schemaName;
      } else if (this.translateContext) {
        context = this.translateContext;
      } else {
        return of(key);
      }

      getKey = `T.COMPONENTS.${context.replace('COMPONENTS.', '').toUpperCase()}.${key.replace(' ', '_').toUpperCase()}`;
    }

    return this.translateService.get(getKey, interpolateParams).pipe(map((lang) => {
      if (lang === getKey) {
        lang = key;
      }
      return lang;
    })) as Observable<string | object>;
  }


  translateText(text: string): Observable<string | object> {
    if (!text) {
      return of(text);
    }
    const textParts = text.split('_');
    if (textParts.length === 2) {
      return this.t(textParts[1].toUpperCase()).pipe(
        map(v => {
          if (v) {
            return ` (${v})`;
          }
          return '';
        })
      );
    } else {
      return of('');
    }


  }

  getCultureName(code: string): Observable<string | object> {
    if (!code) {
      return of(code);
    }
    return this.t(code.toUpperCase()).pipe(
      map(v => {
        if (!v || v.toString().includes('T.')) {
          v = code;
        }
        return v;
      })
    );
  }

  getLanguageName(code: string): Observable<string | object> {
    if (!code) {
      return of(code);
    }
    return this.t(code.toUpperCase()).pipe(
      map(v => {
        if (!v) {
          v = code;
        }
        return v.toString().replace('t.', '');
      })
    );
  }

  getCurrentCulture(): string {
    return this.translateService.currentLang;
  }

  getDefaultCulture(): string {
    return this.translateService.defaultLang;
  }

  getLanguageCode(languageModel: LanguageModel): string {

    if (!languageModel || !languageModel.data) return '';
    return languageModel.data.code.iv;
  }

  fixFieldValue(field: Field, value: any): any {
    if (field.properties.editor === "DateTime") {
      return new SquidexDate(value).format();
    } else if (field.properties.editor === "Dropdown" && !Array.isArray(value)) {
      return [value];
    }
    return value;
  }

  protected validateAllFormFields(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      if (control instanceof FormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof FormGroup) {
        this.validateAllFormFields(control);
      }
    });
  }

  // TODO: Refactor that
  protected generateFormGroup(facade: BaseFacade, fields: Field[], hiddenFields: Array<string>, content: Content = null, isDraft: boolean = false, formValidationsService: FormValidationsService = null): any {
    const formGroup: any = {};

    const unhiddenFields = fields.filter(field => !hiddenFields.includes(field.name));

    for (let field of unhiddenFields) {

      if (field.partitioning === 'language') {
        for (const language of this.availableLanguages.map(languages => languages.data.code.iv)) {

          const fieldName = `${field.name}_${language}`;
          const fieldValue = this.dParser.getData(content, field, language);
          const control = new FormControl(fieldValue);
          const isRequired = field.properties.isRequired;
          let validators: ValidatorFn[] = [];

          if (isRequired) {
            validators.push(Validators.required);
          }

          if (field.properties.maxLength) {
            const maxLengthValidator = Validators.maxLength(field.properties.maxLength);
            validators.push(maxLengthValidator);
          }

          if (field.properties.minLength) {
            const minLengthValidator = Validators.minLength(field.properties.minLength);
            validators.push(minLengthValidator);
          }




          //TODO: When refactor manage pattern
          //custom pattern
          if (field.properties.pattern && formValidationsService) {
            validators = validators.concat(formValidationsService.getValidators(control, field.properties.pattern));
          }

          if (validators.length > 0) {
            control.setValidators(validators);
          }

          formGroup[fieldName] = control;

        }

      } else {

        let fieldValue: any = null;

        if (content) {
          fieldValue = this.dParser.getData(content, field, "iv");
        }

        if (!fieldValue && field.properties.editor === "Toggle") {
          fieldValue = false;
        }

        const control = new FormControl(fieldValue);

        let validators: ValidatorFn[] = [];


        if (field.properties.isRequired) {
          validators.push(Validators.required);
        }

        if (field.properties.maxLength) {
          const maxLengthValidator = Validators.maxLength(field.properties.maxLength);
          validators.push(maxLengthValidator);
        }

        if (field.properties.minLength) {
          const minLengthValidator = Validators.minLength(field.properties.minLength);
          validators.push(minLengthValidator);
        }

        if(field.properties.editor === 'List' && field.properties.fieldType === 'References') {
          //Specific for radio button. Value is not an array of references in this very specific context
          control.setValue(fieldValue[0]);
        }

        //TODO: When refactor manage pattern
        if (field.properties.pattern && formValidationsService) {
          validators = validators.concat(formValidationsService.getValidators(control, field.properties.pattern));
        }

        control.setValidators(validators);

        formGroup[field.name] = control;
      }
    }

    return formGroup;
  }

  isPublishedContent(content: Content): boolean {
    return content && content.status.toLowerCase() === 'published';
  }

  isPublishedPage(content: Content): boolean {
    return (content && content.status.toLowerCase() === 'published') || (content.data.isactive.iv === true);
  }

  getNumberOfDays(date: Date): number {
    return Math.round((new Date().getTime() - date.getTime()) / (1000 * 3600 * 24));
  }

  formatDate(date: string): string {
    const options = <Intl.DateTimeFormatOptions>{ weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(date).toLocaleDateString(this.getCurrentCulture(), options);
  }

  sortByName(a, b) {
    if (a.name < b.name)
      return -1;
    if (a.name > b.name)
      return 1;
    return 0;
  }

  sortByIndex(a, b) {
    if (!a || !a.name) {
      return 0;
    }
    if (a.name < b.name)
      return -1;
    if (a.name > b.name)
      return 1;
    return 0;
  }

  sortByLabel(a, b) {
    const prepareCompare = val => {
      var r = val.toLowerCase();
      r = r.replace(new RegExp(/\s/g), "");
      r = r.replace(new RegExp(/[àáâãäå]/g), "a");
      r = r.replace(new RegExp(/æ/g), "ae");
      r = r.replace(new RegExp(/ç/g), "c");
      r = r.replace(new RegExp(/[èéêë]/g), "e");
      r = r.replace(new RegExp(/[ìíîï]/g), "i");
      r = r.replace(new RegExp(/ñ/g), "n");
      r = r.replace(new RegExp(/[òóôõö]/g), "o");
      r = r.replace(new RegExp(/œ/g), "oe");
      r = r.replace(new RegExp(/[ùúûü]/g), "u");
      r = r.replace(new RegExp(/[ýÿ]/g), "y");
      r = r.replace(new RegExp(/\W/g), "");
      return r.toUpperCase();
    }

    const comparval1 = prepareCompare(a.properties.label + a.name);
    const comparval2 = prepareCompare(b.properties.label + b.name);
    if (comparval1 < comparval2)
        return -1;
    if (comparval1 > comparval2)
        return 1;
    return 0;
  }


  log(toLog: any) {
    if (environment.production) { return; }
    console.log(toLog);
  }

  valString(strValue: string) {
    if (strValue === null || strValue === undefined) {
      return '';
    } else {
      return strValue;
    }
  }

  valNumber(numberValue: number) {
    if (isNaN(numberValue)) {
      return 0;
    } else {
      return numberValue;
    }
  }

  getTextAreaValue(field: Field, language: string, value: string) {
    if (typeof CKEDITOR !== "undefined" && CKEDITOR && CKEDITOR.instances) {
      let fname = field.name;

      if (language) {
        fname += '_' + language;
      }

      for (const instance in CKEDITOR.instances) {
        let textLang: string = CKEDITOR.instances[instance].element.$.nodeName;
        if (textLang.toLowerCase() === fname.toLowerCase()) {
          return CKEDITOR.instances[instance].getData();
        }
      }
    } else if (value) {
      return value;
    } else {
      return '';
    }
    return null;
  }

  reloadCurrentRoute() {
    const currentUrl = this.router.url;
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate([currentUrl]);
    });
  }


  getSearchObject(filter): object {
    const oSearch = {
      or:
        [
          {
            path: 'data.code.iv',
            op: 'contains',
            value: filter
          }
        ]
    }
    return oSearch;
  }

  // a bit of a patch
  setPreviewUrl(sentUrl = '') {
    setTimeout(() => {
      this.t('PREVIEW_SITE').subscribe(title => {
        if (sentUrl) {
          if (this.siteUrlElem) {
            this.siteUrlElem.href = sentUrl;
          }
        } else {
          if (this.pageUrlElement) {
            if (this.siteUrlElem) {
              this.siteUrlElem.href = window.location.origin + this.pageUrlElement.value;
            }
          }

        }
      });
    }, 1000);
  }



}
